import React from "react";
import { Link } from "react-router-dom";
import cmaLogo from "./assets/Staff/CMA registered member logo.jpg";
import { FaAward, FaCertificate, FaShieldAlt, FaGraduationCap, FaGlobe, FaQuoteLeft } from "react-icons/fa";

const FeaturesSection = () => {
  const qualifications = [
    {
      icon: FaAward,
      title: "Over 7 Years Experience",
      description: "Extensive expertise in bioresonance therapy and holistic healing approaches"
    },
    {
      icon: FaShieldAlt,
      title: "EU-Certified Medical Devices",
      description: "State-of-the-art equipment meeting European medical device standards"
    },
    {
      icon: FaCertificate,
      title: "CMA Member",
      description: "Registered members of the Complementary Medical Association"
    },
    {
      icon: FaGraduationCap,
      title: "Specialized Certifications",
      description: "Certified in BICOM Bioresonance, Mental Health Support, and Addiction Therapy"
    },
    {
      icon: FaGlobe,
      title: "International Seminars",
      description: "Regular participation in international seminars and continuing education"
    }
  ];


  return (
    <section className="py-16 bg-gradient-to-br from-white via-gold/5 to-blue/5">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaShieldAlt className="mr-2" />
            Trust & Credibility
          </div>
          <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            Why Choose Us
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our qualifications, certifications, and commitment to excellence make us the trusted choice for your bioresonance therapy journey
          </p>
        </div>

        {/* Qualifications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {qualifications.map((qual, index) => (
            <div key={index} className="group relative">
              <div className="relative p-4">
                <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-grey/20 h-full relative overflow-hidden group-hover:scale-105">
                  {/* Animated Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold to-yellow opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold to-yellow opacity-0 group-hover:opacity-20 transition-opacity duration-700 blur-xl"></div>
                  
                  {/* Icon Container */}
                  <div className="relative z-10">
                      <div className="w-20 h-20 bg-gradient-to-r from-gold/10 via-transparent to-blue/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-500 shadow-lg relative">
                        <qual.icon className="text-3xl text-gold" />
                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-gold to-yellow rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-dark-grey mb-3 text-center group-hover:text-gold transition-colors duration-300">
                      {qual.title}
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                      {qual.description}
                    </p>
                  </div>
                </div>
                
                {/* Enhanced Creative Borders */}
                <div className="absolute inset-0 border-4 border-gold/40 rounded-2xl transform rotate-2 group-hover:rotate-0 transition-transform duration-700 shadow-xl"></div>
                <div className="absolute inset-3 border-2 border-white/60 rounded-2xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                <div className="absolute inset-6 border border-gold/20 rounded-2xl transform rotate-1 group-hover:rotate-0 transition-transform duration-300"></div>
              </div>
              
              {/* Enhanced Floating Elements */}
              <div className="absolute top-6 right-6 w-3 h-3 bg-gradient-to-r from-gold to-yellow rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 shadow-lg animate-pulse"></div>
              <div className="absolute bottom-6 left-6 w-2 h-2 bg-gradient-to-r from-blue to-purple rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200 shadow-lg animate-pulse"></div>
              
              {/* Decorative Background Shapes */}
              <div className="absolute -top-2 -right-2 w-6 h-6 border-2 border-gold/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-400"></div>
              <div className="absolute -bottom-2 -left-2 w-4 h-4 border-2 border-blue/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-500"></div>
            </div>
          ))}
        </div>

        {/* CMA Logo and Quote Section */}
        <div className="text-center mb-20">
          <div className="relative inline-block">
            <div className="w-48 h-48 mx-auto rounded-full bg-white shadow-2xl flex items-center justify-center border-4 border-gold/20 relative overflow-hidden group hover:scale-105 transition-transform duration-500">
              <img src={cmaLogo} alt="CMA Registered Member" className="w-[85%] h-[85%] object-contain" />
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-gold to-blue opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-lg"></div>
            </div>
            
            {/* Decorative elements around logo */}
            <div className="absolute -top-4 -right-4 w-8 h-8 border-2 border-gold/40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
            <div className="absolute -bottom-4 -left-4 w-6 h-6 border-2 border-blue/40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
            <div className="absolute top-1/2 -right-6 w-4 h-4 bg-gradient-to-r from-gold to-yellow rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-300"></div>
            <div className="absolute bottom-1/2 -left-6 w-3 h-3 bg-gradient-to-r from-blue to-purple rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-400"></div>
          </div>
          
          {/* Quote */}
          <div className="mt-8 max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gold/20 relative">
              <FaQuoteLeft className="text-4xl text-gold/60 absolute top-4 left-4" />
              <p className="text-2xl text-dark-grey italic font-medium leading-relaxed pt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                "I am not a doctor but I will relieve you from the pain"
              </p>
              <div className="mt-4 text-sm text-gray-500 font-semibold">
                — Our Commitment to Your Healing Journey
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-white rounded-2xl p-8 max-w-4xl mx-auto shadow-xl border border-gold/20">
            <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
              Ready to Experience Professional Bioresonance Therapy?
            </h3>
            <p className="text-gray-600 mb-6">
              Join hundreds of satisfied clients who have trusted our certified expertise and EU-approved equipment for their healing journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                style={{
                  boxShadow: '0 8px 20px rgba(184,139,68,0.3)'
                }}
              >
                Schedule Consultation
              </a>
              <Link
                to="/therapy"
                className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
              >
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;